import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch } from "../../utils/api";
import '../../styles/admin/admin-form.css'; 

const DIAS_SEMANA = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

const HORARIO_POR_DEFECTO = {
  lunes: [],
  martes: [],
  miercoles: [],
  jueves: [],
  viernes: [],
  sabado: [],
  domingo: []
};

const INITIAL_FORM_DATA = {
  nombre: '',
  telefono: '',
  municipio: '',
  estado: '',
  direccion: '',
  horario: HORARIO_POR_DEFECTO 
};

export default function FormularioAduana() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id); 

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEditMode) {
      setPageLoading(true);
      setError(null);
      
      authFetch(`/api/Aduanas/${id}`)
        .then(res => {
          if (!res.ok) {
            throw new Error('No se pudieron cargar los datos de la aduana.');
          }
          return res.json();
        })
        .then(data => {
          setFormData({
            nombre: data.nombre || '',
            telefono: data.telefono || '',
            municipio: data.municipio || '',
            estado: data.estado || '',
            direccion: data.direccion || '',
            horario: { ...HORARIO_POR_DEFECTO, ...(data.horario || {}) }
          });
        })
        .catch(err => {
          setError(err.message);
        })
        .finally(() => {
          setPageLoading(false);
        });
    }
  }, [id, isEditMode]); 

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleHorarioChange = (dia, index, campo, value) => {
    const nuevosHorarios = JSON.parse(JSON.stringify(formData.horario));
    nuevosHorarios[dia][index][campo] = value;
    setFormData(prev => ({ ...prev, horario: nuevosHorarios }));
  };

  const addTurno = (dia) => {
    const nuevosHorarios = JSON.parse(JSON.stringify(formData.horario));
    nuevosHorarios[dia].push({ abre: '', cierra: '' });
    setFormData(prev => ({ ...prev, horario: nuevosHorarios }));
  };

  const removeTurno = (dia, index) => {
    const nuevosHorarios = JSON.parse(JSON.stringify(formData.horario));
    nuevosHorarios[dia].splice(index, 1);
    setFormData(prev => ({ ...prev, horario: nuevosHorarios }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const url = isEditMode
      ? `/api/Aduanas/${id}`
      : `/api/Aduanas`;
      
    const method = isEditMode ? 'PUT' : 'POST';

    const payload = {
      ...(isEditMode && { id: id }), 
      nombre: formData.nombre,
      telefono: formData.telefono,
      municipio: formData.municipio,
      estado: formData.estado,
      direccion: formData.direccion,
      horario: formData.horario
    };
    
    try {
      const response = await authFetch(url, {
        method: method,
        body: JSON.stringify(payload), 
      });

      if (!response.ok) {
        const errorText = await response.text(); 
        let errorData;
        try {
          errorData = JSON.parse(errorText); 
        } catch (jsonError) {
          errorData = { message: errorText || 'Error al guardar.' }; 
        }
        throw new Error(errorData.message);
      }

      alert(isEditMode ? 'Aduana actualizada exitosamente' : 'Aduana guardada exitosamente');
      navigate('/admin/aduanas'); 
    } catch (err) {
      setError(err.message);
      alert('Error al guardar: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-container">

      <button className="btn-back" onClick={() => navigate(-1)} disabled={isSubmitting}>
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg>
        Volver
      </button>

      <h1>{isEditMode ? 'Editar Aduana' : 'Añadir Nueva Aduana'}</h1>

      {error && <div className="form-error">Error: {error}</div>}

      {pageLoading && (
        <div className="form-loading">
          Cargando datos de la aduana...
        </div>
      )}

      {!pageLoading && (
        <form onSubmit={handleSubmit}>
          
          <div className="form-group">
            <label htmlFor="nombre">Nombre de la Aduana</label>
            <input
              id="nombre"
              name="nombre"
              type="text"
              placeholder="Ej. Aduana de Veracruz"
              value={formData.nombre}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="telefono">Teléfono</label>
            <input
              id="telefono"
              name="telefono"
              type="tel"
              placeholder="Ej. 229 123 4567"
              value={formData.telefono}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="municipio">Municipio</label>
              <input
                id="municipio"
                name="municipio"
                type="text"
                placeholder="Ej. Veracruz"
                value={formData.municipio}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="estado">Estado</label>
              <input
                id="estado"
                name="estado"
                type="text"
                placeholder="Ej. Veracruz"
                value={formData.estado}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="direccion">Dirección</label>
            <textarea
              id="direccion"
              name="direccion"
              placeholder="Ej. Av. Marina Mercante S/N, Col. Centro"
              rows="3"
              value={formData.direccion}
              onChange={handleChange}
              disabled={isSubmitting}
            ></textarea>
          </div>

          <fieldset>
            <legend>Horarios</legend>
            
            {DIAS_SEMANA.map((dia) => (
              <div key={dia} className="horario-dia">
                <div className="horario-dia-header">
                  <h3>{dia.charAt(0).toUpperCase() + dia.slice(1)}</h3>
                  <button 
                    type="button" 
                    onClick={() => addTurno(dia)} 
                    className="btn-add-turno" 
                    disabled={isSubmitting}
                  >
                    + Añadir turno
                  </button>
                </div>

                {formData.horario[dia].length === 0 && (
                  <p className="horario-cerrado">Cerrado</p>
                )}

                {formData.horario[dia].map((turno, index) => (
                  <div key={index} className="horario-turno">
                    <div className="horario-inputs">
                      <div className="form-group">
                        <label htmlFor={`${dia}-abre-${index}`}>Abre</label>
                        <input
                          id={`${dia}-abre-${index}`}
                          type="time"
                          value={turno.abre}
                          onChange={(e) => handleHorarioChange(dia, index, 'abre', e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor={`${dia}-cierra-${index}`}>Cierra</label>
                        <input
                          id={`${dia}-cierra-${index}`}
                          type="time"
                          value={turno.cierra}
                          onChange={(e) => handleHorarioChange(dia, index, 'cierra', e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <button 
                      type="button" 
                      onClick={() => removeTurno(dia, index)} 
                      className="btn-remove-turno" 
                      title="Eliminar turno"
                      disabled={isSubmitting}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </fieldset>
          
          <button 
            type="submit" 
            className="btn-submit" 
            disabled={isSubmitting || pageLoading}
          >
            {isSubmitting ? 'Guardando...' : (isEditMode ? 'Guardar Cambios' : 'Guardar Aduana')}
          </button>
        </form>
      )} 
    </div>
  );
}